import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Compact monthly publication counter shown next to the avatar in the
 * top bar. Only rendered for authenticated users; hidden entirely for
 * admins and unlimited (Premium) plans where there's nothing to count.
 *
 * Color coding:
 *   blue   → plenty of credits left
 *   amber  → < 25 % remaining
 *   red    → quota exhausted (clicking opens the recharge dialog).
 */
export default function PublicationQuotaBadge({ className = "" }) {
    const { user } = useAuth();
    const [q, setQ] = useState(null);

    useEffect(() => {
        if (!user) { setQ(null); return; }
        let cancelled = false;
        const load = async () => {
            try {
                const r = await api.get("/me/publication-quota");
                if (!cancelled) setQ(r.data);
            } catch { /* ignore */ }
        };
        load();
        const t = setInterval(load, 60000);
        return () => { cancelled = true; clearInterval(t); };
    }, [user]);

    if (!user || !q || q.limit == null) return null; // free tier / admin / premium

    const used = q.used || 0;
    const limit = q.limit;
    const extra = q.extra || 0;
    const remaining = q.remaining ?? Math.max(0, limit - used) + extra;
    const ratio = limit ? used / limit : 0;
    const tone = remaining <= 0
        ? "bg-red-100 text-red-700 ring-red-300"
        : ratio >= 0.75
            ? "bg-amber-100 text-amber-800 ring-amber-300"
            : "bg-primary/10 text-primary ring-primary/30";

    return (
        <Link
            to="/billing"
            data-testid="publication-quota-badge"
            title={`${used}/${limit} publications ce mois${extra ? ` · +${extra} crédit(s)` : ""}`}
            className={`hidden sm:inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${tone} ${className}`}>
            <span data-testid="quota-used">{used}</span>
            <span className="opacity-60">/</span>
            <span data-testid="quota-limit">{limit}</span>
            {extra > 0 && (
                <span className="ml-1 text-[10px] font-semibold opacity-80"
                    data-testid="quota-extra">+{extra}</span>
            )}
        </Link>
    );
}
